import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AllStorablesSchemaSelector from 'samples/AllStorablesSchemaSelector';
import {
  StorableSchemaNamesQuery,
  StorableSchemaNamesQueryVariables,
} from 'samples/storables/editable-fields/__generated/StorableSchemaNamesQuery';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BaseEditableField from 'ui-library/BaseEditableField';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

export interface EditableLocationAllowedChildSchemasFieldProps {
  /** Selected child schema ids. Null and an empty list are semantically equivalent right now; the backend expects
   * null to clear out selected schemas whereas the FE components deal best with lists. The conversion is localized
   * here and otherwise not exposed.
   *
   * This list can also include non schema sentinels that represent selecting all schemas of a particular type -
   * e.g. `any_container_schema`. These must be filtered out with filterToValidAllowedChildSchemaIds.
   */
  selectedSchemaIds: Array<string> | null;
  onSetSelectedSchemaIds: (schemaIds: Array<string> | null) => void;
  isDisabled?: boolean;
  /** If provided, scopes the schema options to just those of the provided registry */
  registryId?: string;
  shouldUseCompactDisplay?: boolean;
  isFullWidth?: boolean;
  dataTestAttrs?: DataTestAttrs;
  /** If isForCreate is true, renders the allowed schemas dropdown input directly. By default, it is wrapped in
   * an editable field that defaults to view mode, but can enter into an edit mode to make changes. */
  isForCreate?: boolean;
}

interface QueriedProps {
  selectedSchemas?: Array<{id: string; name: string}>;
  // The editor always expects a list, so we convert null selectedSchemaIds to an empty list in the wrapper
  // component.
  selectedSchemaIds: Array<string>;
}

interface ViewerProps {
  content: string | JSX.Element;
  compactDisplay: boolean;
  fadeText?: boolean;
}

const Viewer = (props: ViewerProps): JSX.Element => (
  <div
    className={classNames({
      'editableField-text': !props.compactDisplay,
      'u-faded': props.fadeText,
    })}
  >
    {props.content}
  </div>
);

export const STORABLE_SCHEMA_NAMES_QUERY = gql`
  query StorableSchemaNamesQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storableSchemas(ids: $ids) {
      ... on StorableSchemaInterface {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Narrow down selected child schema ids to valid ones to persist, removing any schema sentinel options.
 *
 * This component & child ones keep track of any schema of type sentinel options - e.g. `any_container_schema` -
 * which must be included in the selected state to support both select & deselect. These options are not
 * actually persisted since they just serve as a convenience for easily selecting all schemas of a certain
 * type. This function filters those sentinels out to return valid data to persist.
 *
 * @param selectedSchemaIds Array of selected schema ids
 *
 * @return Array of selected schema ids, without the "any of schema" sentinel options
 */
export function filterToValidAllowedChildSchemaIds(
  selectedSchemaIds: Array<string> | null
): Array<string> | null {
  return selectedSchemaIds != null
    ? selectedSchemaIds.filter((schemaId) => !schemaId.startsWith('any_'))
    : selectedSchemaIds;
}

export default function QueryingEditableLocationAllowedChildSchemasField(
  props: EditableLocationAllowedChildSchemasFieldProps
): JSX.Element | null {
  const selectedSchemaIds = props.selectedSchemaIds ?? [];
  const result = useSkippableBenchlingQuery<StorableSchemaNamesQuery, StorableSchemaNamesQueryVariables>(
    STORABLE_SCHEMA_NAMES_QUERY,
    {
      skip: props.isForCreate,
      variables: {ids: selectedSchemaIds},
    }
  );
  if (result.type === QueryResultType.SUCCESS) {
    const parsedSchemas = result.data.storableSchemas.map((schema) =>
      isInaccessible(schema) ? {id: schema.invalidId, name: '(No access)'} : schema
    );
    return (
      <EditableLocationAllowedChildSchemasField
        {...props}
        selectedSchemas={parsedSchemas}
        selectedSchemaIds={selectedSchemaIds}
      />
    );
  } else if (result.type === QueryResultType.SKIPPED) {
    return <EditableLocationAllowedChildSchemasField {...props} selectedSchemaIds={selectedSchemaIds} />;
  } else if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else {
    return null;
  }
}

function EditableLocationAllowedChildSchemasField(
  props: EditableLocationAllowedChildSchemasFieldProps & QueriedProps
): JSX.Element {
  const [editingSchemaIds, setEditingSchemaIds] = useState<Array<string>>(props.selectedSchemaIds);
  const [isBeingEdited, setIsBeingEdited] = useState<boolean>(false);

  useEffect(() => {
    setEditingSchemaIds(props.selectedSchemaIds);
  }, [props.selectedSchemaIds]);

  const handleSetSelectedSchemaIds = (selectedSchemaIds: Array<string>): void => {
    // The editor always expects a list, but we use "null" to clear schema ids entirely, so this component
    // translates between empty list -> null here and when taking in the selectedSchemaIds prop.
    const schemaIdsToSet = !selectedSchemaIds.length ? null : selectedSchemaIds;
    props.onSetSelectedSchemaIds(schemaIdsToSet);
  };

  const handleCancel = (): void => {
    setIsBeingEdited(false);
    setEditingSchemaIds(props.selectedSchemaIds);
  };

  const handleComplete = (): void => {
    // The filtered ids must be non-null since editingSchemaIds cannot be null
    const validSchemaIds = filterToValidAllowedChildSchemaIds(editingSchemaIds)!;
    handleSetSelectedSchemaIds(validSchemaIds);
    setEditingSchemaIds(validSchemaIds);
    setIsBeingEdited(false);
  };

  const sharedProps = {
    registryId: props.registryId,
    includeStorableTypes: [
      ModelTypeConstants.CONTAINER as const,
      ModelTypeConstants.BOX as const,
      ModelTypeConstants.PLATE as const,
    ],
    shouldShowAnySchemaOfTypeOptions: true,
  };

  if (props.isForCreate) {
    return (
      <div className={css(styles.dropdown)}>
        <AllStorablesSchemaSelector
          {...sharedProps}
          isMulti={true}
          onChangeSelectedSchemaIds={handleSetSelectedSchemaIds}
          selectedSchemaIds={props.selectedSchemaIds}
          shouldShowNoSchemaOption={false}
        />
      </div>
    );
  }

  return (
    <BaseEditableField
      editor={
        <div className={css(styles.dropdown)}>
          <AllStorablesSchemaSelector
            {...sharedProps}
            isMulti={true}
            onChangeSelectedSchemaIds={setEditingSchemaIds}
            selectedSchemaIds={editingSchemaIds}
            shouldShowNoSchemaOption={false}
          />
        </div>
      }
      isBeingEdited={isBeingEdited}
      isWritable={!props.isDisabled}
      viewer={
        <Viewer
          content={
            props.selectedSchemas?.length ? props.selectedSchemas.map(({name}) => name).join(', ') : 'All'
          }
          compactDisplay={props.shouldUseCompactDisplay ?? false}
          fadeText={!props.selectedSchemas?.length}
        />
      }
      onCancel={handleCancel}
      onComplete={handleComplete}
      onEdit={() => {
        setIsBeingEdited(true);
      }}
      isFullWidth={props.isFullWidth}
      isCompleteEnabled={true}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

const styles = StyleSheet.create({
  dropdown: {
    flex: '1',
    whiteSpace: 'normal',
  },
});
